// apps/provider/src/services/api/scribe.js
// PrismScribe — recordings, transcription, AI documentation generation, and templates.
//
// Methods: saveRecording, updateRecording, deleteRecording, updateRecordingTranscript,
//          transcribeAndGenerate, generateDocumentation, getTemplates, getTemplate,
//          getNoteTemplates, createNoteTemplate, updateNoteTemplate, cloneNoteTemplate
//
// Xano staging files: 927_templates_GET.xs, 926_templates_template_id_GET.xs, 928_templates_POST.xs, 929_templates_template_id_PATCH.xs
// Response types: @prism-ehr/shared → Recording, TranscriptionResult, ScribeTemplate, NoteTemplateListItem

/**
 * @returns {Object} Scribe domain methods (bound to `this` of the composed apiService)
 */
export function createScribeMethods() {
  return {

    // ============ Recordings ============

    /**
     * Save a new recording (multipart/form-data with audio file).
     *
     * Xano: POST /recordings
     * Xano staging file: 936_recordings_POST.xs
     *
     * Accepts FormData with: encounter_id, transcript, duration, segments, audio (file).
     * Xano stores the audio via storage.create_attachment with public access.
     *
     * Direct-upload clients send a JSON string body instead (metadata only, or
     * metadata + `audio_object` describing the GCS object they uploaded).
     *
     * @param {FormData|string} formData - FormData with audio, or a JSON string body
     * @returns {Promise<import('@prism-ehr/shared').Recording>}
     */
    saveRecording(formData) {
      return this.request('/recordings', {
        method: 'POST',
        timeoutMs: 300000,
        body: formData
      }, 'saveRecording');
    },

    /**
     * Update a recording (multipart/form-data, may include new audio file).
     *
     * Xano: PATCH /recording/{recording_id}
     * Xano staging file: 934_recording_recording_id_PATCH.xs
     *
     * If audio file is included, replaces the stored audio. Otherwise updates
     * only the provided fields (encounter_id, transcript, duration, segments).
     *
     * @param {string} recordingId
     * @param {FormData} formData
     * @returns {Promise<import('@prism-ehr/shared').Recording>}
     */
    updateRecording(recordingId, formData) {
      return this.request(`/recording/${recordingId}`, {
        method: 'PATCH',
        timeoutMs: 300000,
        body: formData
      }, 'updateRecording');
    },

    /**
     * Delete a recording.
     *
     * Xano: DELETE /recording/{recording_id}
     * Xano staging file: 930_recording_recording_id_DELETE.xs
     *
     * @param {string} recordingId
     * @returns {Promise<null>}
     */
    deleteRecording(recordingId) {
      return this.request(`/recording/${recordingId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Update only the transcript field on a recording (JSON body).
     *
     * Xano: PATCH /recording/{recording_id}
     * Xano staging file: 934_recording_recording_id_PATCH.xs
     *
     * Uses the same PATCH endpoint as updateRecording but sends JSON
     * instead of FormData, updating only the transcript.
     *
     * @param {string} recordingId
     * @param {string} transcript
     * @returns {Promise<import('@prism-ehr/shared').Recording>}
     */
    updateRecordingTranscript(recordingId, transcript) {
      return this.request(`/recording/${recordingId}`, {
        method: 'PATCH',
        body: JSON.stringify({ transcript })
      });
    },

    /**
     * Mint a single-object GCS resumable-upload session for direct browser upload
     * (post-8/27 outage: no scribe audio bytes cross an API worker).
     *
     * Xano: POST /scribe/upload-session
     * Xano files: scribe_upload-session_POST.xs (both envs)
     *
     * @param {{ encounter_id: string, recording_id: string, segment_index: number, content_type: string, content_length: number, origin: string }} data
     * @returns {Promise<{ session_url: string, bucket: string, object_name: string, gs_uri: string, content_type: string, segment_index: number, expires_in_seconds: number }>}
     */
    createScribeUploadSession(data) {
      return this.request('/scribe/upload-session', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'createScribeUploadSession');
    },

    // ============ AI Transcription & Documentation ============

    /**
     * Transcribe audio and generate template-driven clinical documentation.
     *
     * Xano: POST /ai/transcribe-ehr
     * Xano staging file: ai_transcribe_ehr_POST.xs
     *
     * Sends audio + template definition to Xano. The endpoint transcribes via
     * Gemini, then uses OpenAI to extract field values per template section.
     * Returns immediately with processing status; results stored on encounter.
     *
     * @param {Object} data
     * @param {string} data.recording_id - Saved recording row (required — status lifecycle anchor)
     * @param {string} [data.audio] - Base64 data-URL audio (legacy single-file; kept for compat)
     * @param {string[]} [data.audio_segments] - Ordered base64 data-URLs, one per recorder session (preferred)
     * @param {string} [data.encounter_id] - Encounter to update with results
     * @param {string} [data.clinical_note_id] - Note whose sections_data receives the merge
     * @param {Object} data.template - Full NoteTemplate with sections[].fields[]
     * @param {Object} [data.existing_field_values] - Keyed by fieldId
     * @returns {Promise<{ success: boolean, status: string, error?: string }>}
     */
    transcribeForEHR(data) {
      // Discharge is a provider choice, not a field for Scribe to populate.
      const template = data.template?.sections ? {
        ...data.template,
        sections: data.template.sections.map(section => ({
          ...section,
          fields: (section.fields || []).filter(field => field.systemAction !== 'discharge_case'),
        })),
      } : data.template;
      return this.request('/ai/transcribe-ehr', {
        method: 'POST',
        body: JSON.stringify({ ...data, template })
      }, 'transcribeForEHR');
    },

    /**
     * Billing code recommendations + documentation compliance score for a
     * documented note (computed on demand, nothing persisted).
     *
     * Xano: POST /ai/note-review (function ai/review_note)
     * Xano file: ai_note-review_POST.xs
     *
     * @param {Object} data - One of the two ids
     * @param {string} [data.clinical_note_id]
     * @param {string} [data.encounter_id]
     * @returns {Promise<{ time_basis: Object, recommended_codes: Array<{code: string, description: string, units: number, minutes: number|null, timed: boolean, modifiers: string[], rationale: string, alternative_to: string|null, rate: number|null, rate_source: string|null, line_total: number|null}>, estimated_total: number, unpriced_codes: string[], rate_basis: string, payer_category_used: string, missed_opportunities: Array<{code: string, what_to_document: string}>, compliance: { score: number, summary: string, items: Array<{severity: string, area: string, issue: string, fix: string}> } }>}
     */
    reviewNote(data) {
      return this.request('/ai/note-review', {
        method: 'POST',
        timeoutMs: 180000,
        body: JSON.stringify(data)
      }, 'reviewNote');
    },

    /**
     * Trigger AI transcription and documentation generation from audio.
     *
     * Xano: POST /ai/transcribe-development
     * Xano staging file: Not found in staging (closest: 822_ai_transcribe_POST.xs for POST /ai/transcribe)
     *
     * Sends audio data to OpenAI Whisper for transcription. The staging
     * endpoint (822) returns { text, duration } from whisper-1 model.
     *
     * @param {Object} data
     * @param {string} [data.audio] - Base64-encoded audio or file reference
     * @param {File} [data.file] - Audio file for transcription
     * @returns {Promise<import('@prism-ehr/shared').TranscriptionResult>}
     */
    transcribeAndGenerate(data) {
      return this.request('/ai/transcribe-development', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'transcribeAndGenerate');
    },

    /**
     * Generate clinical documentation (e.g. SOAP note) from a transcript.
     *
     * Xano: POST /ai/generate-documentation
     * Xano staging file: Not found in staging (closest: 824_ai_generate_soap_POST.xs for POST /ai/generate-soap)
     *
     * The staging endpoint (824) is a streaming endpoint that sends the
     * transcript to OpenAI as a "medical scribe" and streams back the
     * structured clinical note.
     *
     * @param {Object} data
     * @param {string} [data.prompt] - The transcript / instructions for documentation generation
     * @param {string} [data.mode] - e.g. "rehabilitation", "fitness"
     * @param {Object} [data.parameters] - Additional generation parameters
     * @param {string} [data.model] - OpenAI model to use
     * @returns {Promise<Object>} Streamed or complete documentation response
     */
    generateDocumentation(data) {
      return this.request('/ai/generate-documentation', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'generateDocumentation');
    },

    // ============ Templates ============

    /**
     * Get all scribe templates for an organization and user.
     * Returns templates created by the user plus master templates.
     *
     * Xano: GET /templates
     * Xano staging file: 927_templates_GET.xs
     *
     * Filter: organization_id match AND (created_by == auth user OR is_master).
     * Note: List response omits field_mappings and conditional_triggers
     * (use getTemplate for full record).
     *
     * @param {string} organizationId
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').ScribeTemplate[]>}
     */
    getTemplates(organizationId, userId) {
      return this.request(
        `/templates?organization_id=${organizationId}&user_id=${userId}`,
        { method: 'GET' },
        'getTemplates'
      );
    },

    /**
     * Get a single scribe template by ID (full record with field_mappings
     * and conditional_triggers).
     *
     * Xano: GET /templates/{template_id}
     * Xano staging file: 926_templates_template_id_GET.xs
     *
     * @param {string} templateId
     * @returns {Promise<import('@prism-ehr/shared').ScribeTemplate>}
     */
    getTemplate(templateId) {
      return this.request(
        `/templates/${templateId}`,
        { method: 'GET' },
        'getTemplate'
      );
    },

    // ============ Note Templates (Clinical Note Template System) ============

    /**
     * Get note templates for an organization, with optional filters.
     * Returns lightweight list items (omits field_mappings, conditional_triggers, sections).
     *
     * Xano: GET /templates with note_type/specialty filters
     *
     * @param {string} organizationId
     * @param {string} userId
     * @param {{ note_type?: string, specialty?: string }} [filters]
     * @returns {Promise<import('@prism-ehr/shared').NoteTemplateListItem[]>}
     */
    getNoteTemplates(organizationId, userId, filters = {}) {
      const params = new URLSearchParams({
        organization_id: organizationId,
        user_id: userId
      });
      if (filters.note_type) params.append('note_type', filters.note_type);
      if (filters.specialty) params.append('specialty', filters.specialty);

      return this.request(
        `/templates?${params.toString()}`,
        { method: 'GET' },
        'getNoteTemplates'
      );
    },

    /**
     * Create a new note template.
     *
     * Xano: POST /templates
     *
     * @param {Object} templateData - Template fields including name, note_type, sections, etc.
     * @returns {Promise<import('@prism-ehr/shared').ScribeTemplate>}
     */
    createNoteTemplate(templateData) {
      return this.request('/templates', {
        method: 'POST',
        body: JSON.stringify(templateData)
      }, 'createNoteTemplate');
    },

    /**
     * Update an existing note template.
     *
     * Xano: PATCH /templates/{template_id}
     *
     * @param {string} templateId
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').ScribeTemplate>}
     */
    updateNoteTemplate(templateId, data) {
      return this.request(`/templates/${templateId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'updateNoteTemplate');
    },

    /**
     * Publish a note template's draft. Snapshots the outgoing live body into
     * template_version, promotes draft_sections/draft_variables to live,
     * bumps version, and clears the draft fields. Publish is the only writer
     * of live sections in the draft workflow.
     *
     * Xano: POST /template-publish (template-publish_POST.xs, both envs)
     *
     * @param {string} templateId
     * @returns {Promise<import('@prism-ehr/shared').ScribeTemplate>} Updated template
     */
    publishNoteTemplate(templateId) {
      return this.request('/template-publish', {
        method: 'POST',
        body: JSON.stringify({ template_id: templateId })
      }, 'publishNoteTemplate');
    },

    /**
     * Discard a note template's unpublished draft. Live is untouched.
     *
     * Xano: POST /template-discard-draft (template-discard-draft_POST.xs, both envs)
     *
     * @param {string} templateId
     * @returns {Promise<import('@prism-ehr/shared').ScribeTemplate>} Updated template
     */
    discardNoteTemplateDraft(templateId) {
      return this.request('/template-discard-draft', {
        method: 'POST',
        body: JSON.stringify({ template_id: templateId })
      }, 'discardNoteTemplateDraft');
    },

    /**
     * List a template's published version history (metadata only; no bodies).
     *
     * Xano: GET /template-versions (template-versions_GET.xs, both envs)
     *
     * @param {string} templateId
     * @returns {Promise<Array<{id: string, created_at: number, template_id: string, version: number, published_by: string}>>}
     */
    getNoteTemplateVersions(templateId) {
      return this.request(
        `/template-versions?template_id=${templateId}`,
        { method: 'GET' },
        'getNoteTemplateVersions'
      );
    },

    /**
     * Fetch one published template version in full (sections + variables).
     *
     * Xano: GET /template-version (template-version_GET.xs, both envs)
     *
     * @param {string} versionId
     * @returns {Promise<Object>} template_version row
     */
    getNoteTemplateVersion(versionId) {
      return this.request(
        `/template-version?version_id=${versionId}`,
        { method: 'GET' },
        'getNoteTemplateVersion'
      );
    },

    /**
     * Restore a published version's body into the template's DRAFT fields.
     * Live is never touched; the admin publishes the restored draft to make
     * it live.
     *
     * Xano: POST /template-restore (template-restore_POST.xs, both envs)
     *
     * @param {string} templateId
     * @param {string} versionId
     * @returns {Promise<import('@prism-ehr/shared').ScribeTemplate>} Updated template
     */
    restoreNoteTemplateVersion(templateId, versionId) {
      return this.request('/template-restore', {
        method: 'POST',
        body: JSON.stringify({ template_id: templateId, version_id: versionId })
      }, 'restoreNoteTemplateVersion');
    },

    /**
     * Counts of clinical notes referencing a template (blast radius for the
     * builder header and publish confirm).
     *
     * Xano: GET /template-usage (template-usage_GET.xs, both envs)
     *
     * @param {string} templateId
     * @returns {Promise<{total: number, signed: number}>}
     */
    getNoteTemplateUsage(templateId) {
      return this.request(
        `/template-usage?template_id=${templateId}`,
        { method: 'GET' },
        'getNoteTemplateUsage'
      );
    },

    /**
     * Duplicate a template (fetches original, creates copy with new ownership).
     *
     * Xano: GET /templates/{template_id} + POST /templates
     * Xano staging files: 926_templates_template_id_GET.xs, 928_templates_POST.xs
     *
     * @param {string} templateId
     * @param {Object} [overrides] - Optional overrides (e.g. name, organization_id)
     * @returns {Promise<import('@prism-ehr/shared').ScribeTemplate>}
     */
    async cloneNoteTemplate(templateId, overrides = {}) {
      const original = await this.getTemplate(templateId);
      return this.createNoteTemplate({
        name: `${original.name} (Copy)`,
        organization_id: original.organization_id,
        ehr_type: original.ehr_type,
        field_mappings: original.field_mappings,
        conditional_triggers: original.conditional_triggers,
        note_type: original.note_type,
        specialty: original.specialty,
        specialties: original.specialties,
        description: original.description,
        sections: original.sections,
        carry_forward_note_types: original.carry_forward_note_types || [],
        is_default: false,
        is_master: false,
        master_template_id: original.is_master ? original.id : original.master_template_id,
        version: 1,
        ...overrides
      });
    },

  };
}
